import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Film, X } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { BUNNY_CDN_HOSTNAME } from '../utils/video-cdn';
import { devLog, devError } from '../utils/logger';

interface VideoPlayerProps {
  videoId: string;
  cdnHostname?: string;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
  controls?: boolean;
  accessToken?: string | null;
  /** When true (default), use direct CDN URLs only – no signed-URL or status API calls. Set false if Bunny token auth is enabled. */
  preferDirectUrls?: boolean;
  onMetadataLoaded?: (aspectRatio: number) => void;
}

export function VideoPlayer({
  videoId,
  cdnHostname = BUNNY_CDN_HOSTNAME,
  className = '',
  autoPlay = false,
  muted = false,
  controls = true,
  accessToken,
  preferDirectUrls = true,
  onMetadataLoaded,
}: VideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted); // ✅ Use prop value
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [videoStatus, setVideoStatus] = useState<number | null>(null);
  const [encodeProgress, setEncodeProgress] = useState(0);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false); // ✅ Track if video has ever played
  const [isFullscreen, setIsFullscreen] = useState(false); // Track fullscreen state
  const pollIntervalRef = useRef<number | null>(null);
  
  // ✅ SIMPLIFIED: Standard ref - no callback needed with lazy rendering
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // ✅ CRITICAL FIX: Track polling attempts to prevent infinite polling
  const pollAttemptsRef = useRef<number>(0);
  const MAX_POLL_ATTEMPTS = 100; // 100 attempts × 3s = 5 minutes max
  
  // ✅ NEW: Prevent multiple concurrent loadVideo calls
  const isLoadingVideoRef = useRef<boolean>(false);
  const lastLoadedUrlRef = useRef<string>('');

  // 🔒 GDPR: Signed URLs from backend (refreshed every hour)
  // ⚠️ NOTE: Currently optional - Bunny.net has token auth disabled
  const [signedUrls, setSignedUrls] = useState<{
    hlsUrl: string;
    mp4Url: string;
    thumbnailUrl: string;
    expiresAt: number;
  } | null>(null);

  // ✅ FIXED: Generate fallback URLs immediately (token auth is disabled in Bunny.net)
  const hlsUrl = signedUrls?.hlsUrl || `https://${cdnHostname}/${videoId}/playlist.m3u8`;
  const mp4Url = signedUrls?.mp4Url || `https://${cdnHostname}/${videoId}/play_720p.mp4`;
  const thumbnailUrl = signedUrls?.thumbnailUrl || undefined;

  // Fetch signed URLs from backend only when Bunny token auth is enabled (preferDirectUrls === false)
  useEffect(() => {
    if (preferDirectUrls || !accessToken) {
      if (preferDirectUrls) devLog('VideoPlayer: Using direct CDN URLs (preferDirectUrls)');
      return;
    }

    const fetchSignedUrls = async () => {
      try {
        devLog(`🔒 Fetching signed URLs for video: ${videoId}`);
        const response = await fetch(
          `${API_BASE}/videos/${videoId}/signed-urls`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          devError(`❌ Failed to fetch signed URLs: ${response.status}`);
          const errorText = await response.text();
          devError(`Error response: ${errorText}`);
          // ✅ FIXED: Don't set error - video will work with fallback URLs
          return;
        }

        const data = await response.json();
        devLog('✅ Signed URLs fetched successfully:', data);
        setSignedUrls({
          hlsUrl: data.hlsUrl,
          mp4Url: data.mp4Url,
          thumbnailUrl: data.thumbnailUrl,
          expiresAt: data.expiresAt,
        });
      } catch (err) {
        devError('❌ Error fetching signed URLs:', err);
        // ✅ FIXED: Don't set error - video will work with fallback URLs
      }
    };

    fetchSignedUrls();

    // Refresh URLs every 55 minutes (before 1 hour expiry)
    const refreshInterval = setInterval(fetchSignedUrls, 55 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [videoId, accessToken, preferDirectUrls]);

  // Poll video status if encoding (only when using signed URLs flow)
  useEffect(() => {
    if (preferDirectUrls || !accessToken || videoStatus === 3) return;

    // ✅ CRITICAL FIX: Reset attempts counter when effect re-runs
    pollAttemptsRef.current = 0;

    const checkStatus = async () => {
      // ✅ CRITICAL FIX: Increment and check max attempts
      pollAttemptsRef.current += 1;
      
      if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
        devError(`Video encoding timeout after ${MAX_POLL_ATTEMPTS} attempts (${MAX_POLL_ATTEMPTS * 3}s)`);
        setError(true);
        setIsLoading(false);
        
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        return;
      }
      
      try {
        const response = await fetch(
          `${API_BASE}/videos/${videoId}/status`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`, // ✅ FIXED: Use user access token, not anon key
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setVideoStatus(data.status);
          setEncodeProgress(data.encodeProgress || 0);

          // If finished encoding, try to load video
          if (data.status === 3) {
            setIsLoading(true);
            const video = videoRef.current;
            if (video) {
              video.load();
            }
            
            // ✅ CRITICAL FIX: Stop polling when encoding complete
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }
        }
      } catch (error) {
        devError('Failed to check video status:', error);
      }
    };

    // Initial check
    checkStatus();

    // Poll every 3 seconds if encoding
    if (videoStatus !== 3) {
      pollIntervalRef.current = window.setInterval(checkStatus, 3000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [videoId, accessToken, videoStatus, preferDirectUrls]);

  // Listen for fullscreen changes to update state (standard API + iOS webkit)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    const video = videoRef.current;
    const v = video as HTMLVideoElement & { webkitEnterFullscreen?: () => void; webkitDisplayingFullscreen?: boolean };
    if (video && typeof v.webkitEnterFullscreen === 'function') {
      const onWebKitBegin = () => setIsFullscreen(true);
      const onWebKitEnd = () => setIsFullscreen(false);
      video.addEventListener('webkitbeginfullscreen', onWebKitBegin);
      video.addEventListener('webkitendfullscreen', onWebKitEnd);
      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        video.removeEventListener('webkitbeginfullscreen', onWebKitBegin);
        video.removeEventListener('webkitendfullscreen', onWebKitEnd);
      };
    }
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // 🎬 Load video immediately (direct URLs when preferDirectUrls, else signed when available) - don't wait for signed URLs (token auth disabled)
  useEffect(() => {
    // ✅ FIX 4: Prevent race conditions - skip if already loading
    if (isLoadingVideoRef.current) {
      devLog('⏭️ Skipping video load - already in progress');
      return;
    }
    
    let attemptCount = 0;
    const MAX_ATTEMPTS = 50; // Try for 5 seconds (50 * 100ms)
    
    const tryLoadVideo = () => {
      const video = videoRef.current;
      
      if (!video) {
        attemptCount++;
        if (attemptCount < MAX_ATTEMPTS) {
          devLog(`⏳ Waiting for video element... (attempt ${attemptCount}/${MAX_ATTEMPTS})`);
          setTimeout(tryLoadVideo, 100); // Retry every 100ms
        } else {
          devError('❌ Video element never mounted - gave up after 5 seconds');
          setError(true);
          isLoadingVideoRef.current = false;
        }
        return;
      }
      
      // ✅ FIX 4: Mark as loading to prevent race conditions
      isLoadingVideoRef.current = true;
      
      devLog('🔄 Video element ready - loading video!');
      devLog('🎬 Video loading started!');
      
      // Check if browser supports HLS natively (Safari)
      const canPlayHLS = video.canPlayType('application/vnd.apple.mpegurl');
      devLog('Browser HLS support:', canPlayHLS);

      if (canPlayHLS) {
        // Safari - native HLS support
        devLog(`🎬 Loading HLS: ${hlsUrl}`);
        video.src = hlsUrl;
      } else {
        // Other browsers - use MP4 fallback
        devLog(`🎬 Loading MP4: ${mp4Url}`);
        video.src = mp4Url;
      }

      video.load();

      // ✅ FIXED: Only autoplay if explicitly requested AND after delay
      if (autoPlay) {
        // Delay to ensure modal/container fade-in is complete (AnimatePresence = 200-300ms)
        setTimeout(async () => {
          try {
            await video.play();
            devLog('✅ Autoplay started successfully');
            setIsPlaying(true);
            setHasStartedPlaying(true);
          } catch (err) {
            // ✅ FIX 2: Add error logging when play fails
            devError('❌ Autoplay failed - user interaction required:', err);
            setIsPlaying(false);
          } finally {
            // ✅ FIX 4: Release loading lock after autoplay attempt
            isLoadingVideoRef.current = false;
          }
        }, 500); // ✅ Increased from 100ms to 500ms
      } else {
        // ✅ FIX 4: Release loading lock if no autoplay
        isLoadingVideoRef.current = false;
      }
    };
    
    tryLoadVideo();
  }, [videoId, autoPlay]); // ✅ FIXED: Only reload when videoId or autoPlay changes, not when signed URLs arrive

  // ✅ FIX 5: Add retry logic with proper error handling
  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) {
      devError('❌ togglePlay: No video element found');
      return;
    }

    if (video.paused) {
      // Mobile: request fullscreen in same user gesture so video isn’t tiny in modal
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      if (isMobile) {
        const v = video as HTMLVideoElement & { webkitEnterFullscreen?: () => void };
        if (typeof v.webkitEnterFullscreen === 'function') {
          v.webkitEnterFullscreen();
        } else if (typeof video.requestFullscreen === 'function') {
          video.requestFullscreen().catch(() => {});
        }
      }
      try {
        devLog('▶️ Attempting to play video...');
        await video.play();
        devLog('✅ Video play successful');
        setIsPlaying(true);
        setHasStartedPlaying(true);
      } catch (err) {
        // ✅ FIX 2: Add detailed error logging
        devError('❌ Video play failed:', err);
        devError('Video state:', {
          paused: video.paused,
          currentTime: video.currentTime,
          duration: video.duration,
          readyState: video.readyState,
          networkState: video.networkState,
          src: video.src
        });
        setIsPlaying(false);
        
        // ✅ Retry once after 500ms
        setTimeout(async () => {
          try {
            devLog('🔄 Retrying video play...');
            await video.play();
            devLog('✅ Retry successful');
            setIsPlaying(true);
            setHasStartedPlaying(true);
          } catch (retryErr) {
            devError('❌ Retry failed:', retryErr);
            setIsPlaying(false);
          }
        }, 500);
      }
    } else {
      video.pause();
      setIsPlaying(false);
      devLog('⏸️ Video paused');
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    const v = video as HTMLVideoElement & { webkitEnterFullscreen?: () => void; webkitExitFullscreen?: () => void };
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (isFullscreen && typeof v.webkitExitFullscreen === 'function') {
      v.webkitExitFullscreen();
    } else if (typeof v.webkitEnterFullscreen === 'function') {
      v.webkitEnterFullscreen();
    } else if (typeof video.requestFullscreen === 'function') {
      video.requestFullscreen();
    }
  };

  const handleLoadStart = () => {
    devLog('📹 Video loadstart event fired');
    setIsLoading(true);
  };

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = e.currentTarget;
    if (onMetadataLoaded) onMetadataLoaded(video.videoWidth / video.videoHeight);
    // Show video + play button as soon as metadata is ready (don't wait for canplay)
    setIsLoading(false);
    setVideoStatus(3);
  };

  const handleCanPlay = () => {
    devLog('✅ Video canplay event fired - video is ready to play!');
    setIsLoading(false);
    setVideoStatus(3);
    setError(false);
  };
  
  const handleError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const videoElement = e.currentTarget;
    devLog('❌ Video error event fired:', {
      code: videoElement.error?.code,
      message: videoElement.error?.message,
      src: videoElement.currentSrc,
    });
    setError(true);
    setIsLoading(false);
  };

  const handleRetry = () => {
    const video = videoRef.current;
    if (!video) return;
    setError(false);
    setIsLoading(true);
    const canPlayHLS = video.canPlayType('application/vnd.apple.mpegurl');
    video.src = canPlayHLS ? hlsUrl : mp4Url;
    video.load();
  };

  // Show encoding status if video is encoding (status 0, 1, or 2)
  const isEncoding = videoStatus !== null && videoStatus !== 3 && videoStatus !== 4;
  // ✅ FIX: Only show error if error flag is true AND video hasn't successfully loaded
  // Don't trust videoStatus === 4 from Bunny.net API - it can be wrong
  const hasFailed = error && !isLoading && videoStatus !== 3; // ⚠️ Will be overridden by handleCanPlay if video actually works

  return (
    <div className={`relative rounded-2xl overflow-hidden group flex items-center justify-center ${className}`}>
      {/* Encoding Status Overlay */}
      {isEncoding && (
        <div className="absolute inset-0 bg-gradient-to-br from-orange-100 to-pink-100 rounded-2xl flex items-center justify-center z-10">
          <div className="text-center p-8">
            <div className="w-20 h-20 bg-orange-600/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 relative">
              <Film className="w-10 h-10 text-orange-600" />
              <Loader size={80} className="text-orange-600 absolute inset-0" />
            </div>
            <p className="text-orange-900 font-semibold mb-2">Video konverteres</p>
            <p className="text-sm text-orange-700 mb-3">Dette kan ta noen minutter</p>
            {encodeProgress > 0 && (
              <div className="max-w-xs mx-auto">
                <div className="bg-orange-200 rounded-full h-2 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-orange-500 to-pink-500 h-full transition-all duration-500 rounded-full"
                    style={{ width: `${encodeProgress}%` }}
                  />
                </div>
                <p className="text-xs text-orange-600 mt-2">{Math.round(encodeProgress)}% ferdig</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {hasFailed && !isEncoding && (
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-red-100 rounded-2xl flex items-center justify-center z-10">
          <div className="text-center p-8">
            <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Film className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-red-900 font-medium mb-3">Kunne ikke laste video</p>
            <button
              type="button"
              onClick={handleRetry}
              className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-medium"
            >
              Prøv igjen
            </button>
          </div>
        </div>
      )}

      {/* Video Element - ALWAYS RENDERED so ref can work */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
        style={{ opacity: isLoading ? 0 : 1 }}
        preload="auto"
        muted={isMuted}
        playsInline
        onLoadStart={handleLoadStart}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onError={handleError}
        onPlay={() => {
          setIsPlaying(true);
          setHasStartedPlaying(true);
        }}
        onPause={() => setIsPlaying(false)}
        onClick={togglePlay}
      />

      {/* Loading Spinner - Only show when loading */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
          <Loader size="2xl" className="text-orange-600" />
        </div>
      )}

      {/* Custom Controls (if enabled) */}
      {controls && !isLoading && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 max-md:opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-5">
          <div className="flex items-center gap-4">
            {/* Play/Pause */}
            <button
              onClick={togglePlay}
              className="text-white hover:text-pink-500 transition-colors"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <Pause className="w-6 h-6" />
              ) : (
                <Play className="w-6 h-6" />
              )}
            </button>

            {/* Mute/Unmute */}
            <button
              onClick={toggleMute}
              className="text-white hover:text-pink-500 transition-colors"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <VolumeX className="w-6 h-6" />
              ) : (
                <Volume2 className="w-6 h-6" />
              )}
            </button>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="text-white hover:text-pink-500 transition-colors"
              aria-label="Fullscreen"
            >
              <Maximize className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {/* ✅ Play button overlay - Talentfunn UI style - icon only */}
      {!isPlaying && !isLoading && !isEncoding && !hasFailed && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            togglePlay();
          }}
          className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors z-15"
          aria-label="Play video"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-600 to-pink-600 blur-xl opacity-60" />
            <Play className="relative w-20 h-20 text-pink-600 drop-shadow-2xl hover:scale-110 transition-transform" fill="currentColor" />
          </div>
        </button>
      )}

      {/* Fullscreen Exit Button - Only shown when in fullscreen */}
      {isFullscreen && (
        <button
          onClick={() => {
            const video = videoRef.current;
            const v = video as HTMLVideoElement & { webkitExitFullscreen?: () => void };
            if (video && typeof v.webkitExitFullscreen === 'function') {
              v.webkitExitFullscreen();
            } else if (document.fullscreenElement) {
              document.exitFullscreen().catch((err) => {
                devError('Failed to exit fullscreen:', err);
              });
            }
          }}
          className="fixed top-4 right-4 z-50 w-11 h-11 min-w-11 min-h-11 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-all touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          aria-label="Exit fullscreen"
        >
          <X className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
